import { Action, ActionReducer, MetaReducer } from '@ngrx/store'

import * as PresenterWidgetsActions from './presenter-widgets.actions'
import { UndoRedoState, createUndoRedoState } from './undo-redo.models'

export interface UndoRedoMetaReducerOptions<T> {
  undoableActionTypes: string[]
  historyLimit?: number
  sanitizeState?: (state: T) => T
}

export const undoRedoMetaReducer = <T>(options: UndoRedoMetaReducerOptions<T>): MetaReducer<unknown, Action> => {
  const historyLimit = options.historyLimit ?? 50
  const sanitizeState = options.sanitizeState ?? ((s: T) => s)

  return (reducer: ActionReducer<unknown, Action>): ActionReducer<unknown, Action> => {
    const presentReducer = reducer as ActionReducer<T, Action>

    return (state: unknown, action: Action): unknown => {
      const typedState = state as UndoRedoState<T> | undefined
      if (!typedState) {
        return createUndoRedoState(presentReducer(undefined, action))
      }

      if (action.type === PresenterWidgetsActions.undo.type) {
        if (!typedState.past.length) {
          return typedState
        }

        const previous = sanitizeState(typedState.past[typedState.past.length - 1])
        return {
          past: typedState.past.slice(0, -1),
          present: previous,
          future: [sanitizeState(typedState.present), ...typedState.future],
        }
      }

      if (action.type === PresenterWidgetsActions.redo.type) {
        if (!typedState.future.length) {
          return typedState
        }

        const [next, ...restFuture] = typedState.future
        return {
          past: [...typedState.past, sanitizeState(typedState.present)].slice(-historyLimit),
          present: sanitizeState(next),
          future: restFuture,
        }
      }

      const nextPresent = presentReducer(typedState.present, action)

      if (options.undoableActionTypes.includes(action.type)) {
        if (nextPresent === typedState.present) {
          return typedState
        }
        return {
          past: [...typedState.past, sanitizeState(typedState.present)].slice(-historyLimit),
          present: nextPresent,
          future: [],
        }
      }

      return {
        ...typedState,
        present: nextPresent,
      }
    }
  }
}
